import { cn } from '@/lib/utils';
import soloLogo from '@/assets/solo-logo.png';

interface SoloLogoProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  showText?: boolean;
  variant?: 'default' | 'light';
  className?: string;
  iconClassName?: string;
  textClassName?: string;
}

const sizeConfig = {
  xs: { container: 'w-6 h-6', radius: 'rounded-lg', text: 'text-sm' },
  sm: { container: 'w-8 h-8', radius: 'rounded-xl', text: 'text-lg' },
  md: { container: 'w-10 h-10', radius: 'rounded-xl', text: 'text-2xl' },
  lg: { container: 'w-12 h-12', radius: 'rounded-2xl', text: 'text-3xl' },
  xl: { container: 'w-20 h-20', radius: 'rounded-3xl', text: 'text-5xl' },
};

export function SoloLogo({ 
  size = 'md', 
  showText = true, 
  variant = 'default',
  className,
  iconClassName,
  textClassName 
}: SoloLogoProps) {
  const config = sizeConfig[size];
  
  const gradientClass = variant === 'light' 
    ? 'bg-gradient-to-br from-amber-600 to-orange-700' 
    : 'gradient-gold';
  
  const textGradientClass = variant === 'light'
    ? 'bg-gradient-to-r from-amber-600 to-orange-700 bg-clip-text text-transparent'
    : 'gradient-gold-text';

  return (
    <div className={cn('flex items-center justify-center gap-2.5', className)}>
      <div 
        className={cn(
          config.container,
          config.radius,
          'overflow-hidden relative flex items-center justify-center',
          variant === 'default' && 'shadow-glow',
          iconClassName
        )}
      >
        {/* Use mask to apply gradient color to logo */}
        <div 
          className={cn('w-full h-full', gradientClass)}
          style={{
            WebkitMaskImage: `url(${soloLogo})`,
            WebkitMaskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskImage: `url(${soloLogo})`,
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
          }}
        />
      </div>
      {showText && (
        <span className={cn('font-bold tracking-tight', config.text, textGradientClass, textClassName)}>
          SOLO
        </span>
      )}
    </div>
  );
}

// Simple icon-only version for compact use cases
export function SoloLogoIcon({ 
  size = 'md',
  variant = 'default',
  className 
}: { 
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'light';
  className?: string;
}) {
  return <SoloLogo size={size} showText={false} variant={variant} className={className} />;
}
